"use client";

import { useEffect, useCallback, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { useStore } from "../store";
import { useChannelList, useChannelsLoading } from "../store/selectors";
import type { Channel } from "../store/types";

export type { Channel } from "../store/types";

export function useChannels() {
  const { authenticated, token } = useAuth();
  const channels = useChannelList();
  const loading = useChannelsLoading();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchChannels = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) {
      useStore.getState().setChannels([]);
      return;
    }

    useStore.getState().setChannelsLoading(true);
    try {
      const data = await apiFetch<Channel[]>("/channels", t);
      useStore.getState().setChannels(data);
    } catch {
      useStore.getState().setChannelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchChannels();
    } else {
      useStore.getState().setChannels([]);
    }
  }, [authenticated, fetchChannels]);

  const createChannel = useCallback(
    async (opts: {
      name?: string;
      type?: "dm" | "group" | "broadcast";
      visibility?: "private" | "public";
      description?: string;
    }): Promise<Channel> => {
      const t = tokenRef.current;
      if (!t) throw new Error("Not authenticated");
      const channel = await apiFetch<Channel>("/channels", t, {
        method: "POST",
        body: JSON.stringify(opts),
      });
      await fetchChannels();
      return channel;
    },
    [fetchChannels]
  );

  return { channels, loading, error: null, refetch: fetchChannels, createChannel };
}
